import { Form, Input, Radio } from 'antd'
import { Trans } from 'react-i18next'
import { useTranslation } from 'react-i18next'

import { getBonusStat } from '@/models/credit'
import { useUserStore } from '@/stores/user'
import { formatPrice } from '@/utils/formatter'

import { PaymentCurrencySelector } from './PaymentCurrencySelector'
import { PaymentMethodSelector } from './PaymentMethodSelector'

const FEE = {
  DEFAULT: 100,
  MINIMUM: 10,
} as const

interface RechargeStepProps {
  form: any
  loading: boolean
  feeFocus: 'radio' | 'input' | null
  inputValue: string
  customFeeDescription: string
  onFeeFocusChange: (focus: 'radio' | 'input' | null) => void
  onInputValueChange: (value: string) => void
  onCustomFeeDescriptionChange: (desc: string) => void
  debouncedFetchPaymentAmount: any
  debouncedAutoCorrect: any
  setFeePoint: (point: number) => void
  setPaymentAmount: (amount: number) => void
  setIsCalculating: (calculating: boolean) => void
  balanceKind?: 'total' | 'recharged'
  // Whether transferring platform credits is valid for this recharge flow
  allowPlatformBalanceTransfer?: boolean
  platformBalance?: number
  isPlatformBalanceLoading?: boolean
}

export function RechargeStep(props: RechargeStepProps) {
  const { t } = useTranslation('billing')
  const userStore = useUserStore()
  const {
    form,
    loading,
    feeFocus,
    inputValue,
    customFeeDescription,
    onFeeFocusChange,
    onInputValueChange,
    onCustomFeeDescriptionChange,
    debouncedFetchPaymentAmount,
    debouncedAutoCorrect,
    setFeePoint,
    setPaymentAmount,
    setIsCalculating,
    balanceKind = 'total',
    allowPlatformBalanceTransfer = false,
    platformBalance = 0,
    isPlatformBalanceLoading = false,
  } = props
  const balanceCent =
    balanceKind === 'recharged'
      ? (getBonusStat(userStore.account)?.rechargeBonus.moneyCent ?? 0)
      : (userStore.account.totalBonus.moneyCent ?? 0)

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <div className="t-sb text-with-star">
          <Trans ns="billing" i18nKey="rechargeModal.form.amount">
            Buy Credits
          </Trans>
        </div>
        <div className="t-sm text-secondary py-1 px-4">
          {balanceKind === 'recharged' ? (
            <Trans ns="billing" i18nKey="rechargeModal.rechargedCreditsBalance">
              Recharged Credits
            </Trans>
          ) : (
            <Trans ns="billing" i18nKey="rechargeModal.myBalance">
              My Balance
            </Trans>
          )}
          <span className="ml-1 t-sb text-highlight">{formatPrice(balanceCent, false, '')}</span>
          <span className="t-sm text-secondary">{balanceCent > 100 ? 'Credits' : 'Credit'}</span>
        </div>
      </div>

      <Form.Item
        className="mb-2"
        name="fee"
        rules={[
          {
            validator: (_, value) => {
              const customFee = form.getFieldValue('customFee')
              if (customFee !== undefined && customFee !== '') {
                return Promise.resolve()
              }
              if (feeFocus !== 'radio' && value < FEE.MINIMUM) {
                return Promise.reject(new Error(t('rechargeModal.form.minimumRechargeMsg')))
              }
              return Promise.resolve()
            },
          },
        ]}
      >
        <Radio.Group className="w-full grid grid-cols-4 gap-2">
          {[FEE.DEFAULT, 50, 30, FEE.MINIMUM].map((value) => (
            <Radio
              key={value}
              value={value}
              className="flex justify-center text-center px-4 py-6 h-22 border border-gray100 rounded-lg hover:border-secondary [&_.ant-radio]:hidden [&.ant-radio-wrapper-checked]:border-secondary [&.ant-radio-wrapper-checked]:bg-highlight/10"
              onClick={() => {
                onFeeFocusChange('radio')
                onInputValueChange('')
                form.setFieldsValue({ customFee: undefined })
              }}
            >
              <div className="t-bold text-primary">{value}</div>
              <div className="t-xs text-secondary">Credits</div>
            </Radio>
          ))}
        </Radio.Group>
      </Form.Item>

      <Form.Item
        className="mb-0"
        name="customFee"
        rules={[
          {
            validator: (_, value) => {
              if (value === undefined || value === '') {
                return Promise.resolve()
              }
              const numValue = Number(value)
              if (isNaN(numValue)) {
                return Promise.reject(new Error(t('rechargeModal.form.wrongNumberMsg')))
              }
              if (numValue < FEE.MINIMUM) {
                return Promise.reject(new Error(t('rechargeModal.form.minimumRechargeMsg')))
              }
              return Promise.resolve()
            },
          },
        ]}
      >
        <Input
          size="large"
          suffix={<p className="t-sb text-primary">Credits</p>}
          inputMode="numeric"
          maxLength={8}
          max={999_999_990}
          placeholder={t('rechargeModal.form.amountPlaceholder')}
          className="w-full items-center t-sb px-4 py-3 h-11 border border-gray100 rounded-lg hover:border-secondary"
          onFocus={() => {
            onFeeFocusChange('input')
            form.setFieldsValue({ fee: undefined, customFee: undefined })
            onInputValueChange('')
            debouncedFetchPaymentAmount.cancel()
            setFeePoint(0)
            setPaymentAmount(0)
            setIsCalculating(true)
            onCustomFeeDescriptionChange(t('rechargeModal.form.10xMsg'))
          }}
          onChange={(e) => {
            const value = e.target.value

            if (value === '') {
              debouncedAutoCorrect.cancel()
              debouncedFetchPaymentAmount.cancel()
              form.setFieldsValue({ customFee: undefined, fee: undefined })
              setFeePoint(0)
              onInputValueChange('')
              setIsCalculating(true)
              onCustomFeeDescriptionChange(`ℹ ${t('rechargeModal.form.10xMsg')}`)
              return
            }

            onInputValueChange(value)
            const numValue = Number(value)
            if (!isNaN(numValue)) {
              if (numValue === 0) {
                form.setFieldsValue({ customFee: undefined, fee: undefined })
                setFeePoint(0)
                onCustomFeeDescriptionChange(`ℹ ${t('rechargeModal.form.10xMsg')}`)
                return
              }
              if (numValue <= 99999990) {
                if (numValue % 10 !== 0) {
                  onCustomFeeDescriptionChange(`⚠️ ${t('rechargeModal.form.notMultipleOf10Msg')}`)
                  debouncedAutoCorrect(numValue)
                } else {
                  debouncedAutoCorrect.cancel()
                  form.setFieldsValue({ customFee: numValue, fee: undefined })
                  setFeePoint(numValue)
                  setIsCalculating(true)
                  onCustomFeeDescriptionChange(`ℹ ${t('rechargeModal.form.10xMsg')}`)
                }
              } else {
                debouncedAutoCorrect.cancel()
                form.setFieldsValue({ customFee: 99999990, fee: undefined })
                setFeePoint(99999990)
                setIsCalculating(true)
                onInputValueChange('99999990')
                onCustomFeeDescriptionChange(`✅ ${t('rechargeModal.form.10xMsg')}`)
              }
            }
          }}
          value={feeFocus !== 'radio' ? inputValue : ''}
        />
      </Form.Item>
      <div className="mt-2 t-sm text-secondary mb-4">{customFeeDescription}</div>

      <Form.Item
        label={
          <div className="text-with-star">
            <Trans ns="billing" i18nKey="rechargeModal.form.paymentCurrency">
              Select Your Payment Currency
            </Trans>
          </div>
        }
        className="mb-4"
        name="currency"
        rules={[{ required: true }]}
      >
        <PaymentCurrencySelector disabled={loading} />
      </Form.Item>

      <Form.Item
        label={
          <div className="text-with-star">
            <Trans ns="billing" i18nKey="rechargeModal.form.paymentMethod">
              Payment Method
            </Trans>
          </div>
        }
        className="mb-0"
        name="type"
        rules={[{ required: true }]}
      >
        <PaymentMethodSelector
          disabled={loading}
          currency={form.getFieldValue('currency')}
          value={form.getFieldValue('type')}
          onChange={(e) => form.setFieldsValue({ type: e.target.value })}
          allowPlatformBalanceTransfer={allowPlatformBalanceTransfer}
          platformBalance={platformBalance}
          isPlatformBalanceLoading={isPlatformBalanceLoading}
        />
      </Form.Item>
    </div>
  )
}
